import { t } from '@hefe/i18n';
import { RegistrationActions } from '@hefe/store';
import { Button, Card, Col, Result, Row, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import CustomForm, {
  FormField,
} from '../../../containers/core/components/form/custom-form';
import { useAppDispatch } from '../../../store/store';

/* eslint-disable-next-line */
export interface RegisterPage {}

export function RegisterPage(props: RegisterPage) {
  const dispatch = useAppDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const [validatingToken, setValidatingToken] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [clubName, setClubName] = useState('');
  const [teamName, setTeamName] = useState();

  const [saving, setSaving] = useState(false);
  const [registrationSuccessful, setRegistrationSuccessful] = useState(false);

  useEffect(() => {
    validateToken();
  }, []);

  const validateToken = () => {
    setValidatingToken(true);
    dispatch(
      RegistrationActions.validateRegistrationToken(params['token'] ?? '')
    )
      .unwrap()
      .then((res) => {
        setClubName(res.name);
        setTeamName(res.team);
      })
      .catch((res) => setValidationError(true))
      .finally(() => setValidatingToken(false));
  };

  const handleRegistraion = async (values: any) => {
    const submitValues = {
      ...values,
      token: params['token'] ?? '',
      birthday: dayjs(values.birthday).format('YYYY-MM-DD'),
    };

    setSaving(true);
    dispatch(RegistrationActions.registerUser(submitValues))
      .unwrap()
      .then((result) => setRegistrationSuccessful(true))
      .catch(() => null)
      .finally(() => setSaving(false));
  };

  const fields: FormField[] = [
    {
      name: 'sex',
      type: 'radiogroup',
      label: t('sex.title'),
      placeholder: t('sex.title'),
      selectOptions: [
        { label: t('sex.male'), value: 1 },
        { label: t('sex.female'), value: 2 },
        { label: t('sex.diverse'), value: 3 },
      ],
      rules: [
        {
          required: true,
          type: 'number',
          message: t('form_errors.sex'),
        },
      ],
    },
    {
      name: 'firstname',
      label: t('firstname'),
      type: 'text',
      rules: [
        {
          required: true,
          message: t('form_errors.name'),
        },
      ],
    },
    {
      name: 'lastname',
      label: t('lastname'),
      type: 'text',
      rules: [
        {
          required: true,
          message: t('form_errors.name'),
        },
      ],
    },
    {
      name: 'birthday',
      label: t('birthday'),
      type: 'date',
      rules: [],
    },
    {
      name: 'represented_by',
      label: t('represented_by'),
      type: 'text',
      rules: [],
    },
    {
      name: 'nationality',
      label: t('nationality'),
      type: 'text',
      rules: [],
    },
    {
      name: 'street',
      label: t('street'),
      type: 'text',
      rules: [],
    },
    {
      name: 'zipcode',
      label: t('zipcode'),
      type: 'number',
      rules: [
        {
          type: 'number',
          message: t('form_errors.number'),
        },
        {
          type: 'number',
          min: 1000,
          message: t('form_errors.number_min', { min: 1000 }),
        },
        {
          type: 'number',
          max: 99999,
          message: t('form_errors.number_max', { max: 99999 }),
        },
      ],
    },
    {
      name: 'city',
      label: t('city'),
      type: 'text',
      rules: [],
    },
    {
      name: 'country',
      label: t('country'),
      type: 'text',
      rules: [],
    },
    {
      name: 'phone',
      label: t('phone'),
      type: 'text',
      rules: [],
    },
    {
      name: 'email',
      label: t('email'),
      type: 'email',
      rules: [
        {
          required: true,
          type: 'email',
          message: t('form_errors.email'),
        },
      ],
    },
    {
      name: 'password',
      type: 'password',
      label: t('password'),
      placeholder: t('password'),
      rules: [
        {
          required: true,
          type: 'string',
          message: t('form_errors.password'),
        },
        {
          min: 8,
          message: t('form_errors.password_length'),
        },
      ],
    },
    {
      name: 'password_confirmation',
      type: 'password',
      label: t('password_confirmation'),
      placeholder: t('password_confirmation'),
      rules: [
        {
          required: true,
          type: 'string',
          message: t('form_errors.password'),
        },
        ({ getFieldValue }) => ({
          validator(_, value) {
            if (!value || getFieldValue('password') === value) {
              return Promise.resolve();
            }
            return Promise.reject(new Error(t('form_errors.password_confirm')));
          },
        }),
      ],
    },
    {
      name: 'privacy',
      label: t('privacy'),
      type: 'checkbox',
      extraLabel: (
        <Typography.Text>
          {t('information_texts.read_privacy')}{' '}
          <Link
            to="https://www.backcourt-software.de/privacy-faq"
            target="_blank"
          >
            ({t('privacy')})
          </Link>
        </Typography.Text>
      ),
      rules: [
        {
          required: true,
          type: 'boolean',
          transform: (value) => value || undefined,
          message: t('form_errors.privacy'),
        },
      ],
    },
  ];

  if (validatingToken) {
    return (
      <Row align="middle" justify="center">
        <Col xs={24}>
          <Spin size="large" style={{ width: '100%' }} />
        </Col>
      </Row>
    );
  }

  if (validationError) {
    return (
      <Row align="middle" justify="center">
        <Col md={12} xs={24}>
          <Card>
            <Result
              status="error"
              title={t('error')}
              subTitle={t('errors.token_invalid_registration')}
            />
          </Card>
        </Col>
      </Row>
    );
  }

  if (registrationSuccessful) {
    return (
      <Row align="middle" justify="center">
        <Col md={12} xs={24}>
          <Card>
            <Result
              status="success"
              title="Swish"
              subTitle={t('information_texts.registration_successful')}
              extra={[
                <Button type="primary" onClick={() => navigate('/login')}>
                  {t('back_to_login')}
                </Button>,
              ]}
            />
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <Row align="middle" justify="center">
      <Col md={18} xs={24}>
        <Card>
          <Typography.Title level={2}>
            {t('registration.title')}
          </Typography.Title>
          <Typography.Text>
            {t('registration.subtitle', { name: clubName })}
          </Typography.Text>
          {teamName && (
            <Typography.Text>
              {' '}
              {t('registration.subtitle_team', { name: teamName })}
            </Typography.Text>
          )}
          <Typography.Title level={4}>
            {t('important_information')}
          </Typography.Title>
          <Typography.Paragraph style={{ marginBottom: 20 }}>
            {t('registration.link_accounts_info')}
          </Typography.Paragraph>
          <CustomForm
            fields={fields}
            onSubmit={(values) => handleRegistraion(values)}
            loading={saving}
            submitButtonText={t('send')}
          />
        </Card>
      </Col>
    </Row>
  );
}

export default RegisterPage;
